import { pushToGoogleDataLayer } from '../general/googleAnalytics.js';
import { safeFetch } from '../general/fetchUtils.js';

/** * Escapes special characters in a string for use in a regular expression.
 * @param {string} str - The input string to escape
 * @returns {string} Escaped string suitable for use in a regular expression
 */
function escapeRegExp(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Replaces plus signs with placeholder text to prevent URL encoding issues.
 * @param {string} string - The input string to process
 * @returns {string} String with plus signs replaced by 'xplusx'
 */
function coveoPlusReplace(string) {
    if (string) {
        return string.replace(/\+/g, 'xplusx');
    }
    return string;
}

/**
 * Restores plus signs from placeholder text after processing.
 * @param {string} string - The input string to restore
 * @returns {string} String with 'xplusx' placeholders restored to plus signs
 */
function coveoPlusRestore(string) {
    if (string) {
        return string.replace(/xplusx/g, '+');
    }
    return string;
}

/**
 * Initializes Coveo search interface with configuration and authentication.
 * @param {Object} searchInterface - The Coveo search interface instance
 * @param {Object} searchInterfaceOptions - Configuration options for the search interface
 * @param {boolean} isSecureSearch - Whether to use secure search with token authentication
 * @returns {Promise<void>} Promise that resolves when initialization is complete
 *
 */
async function initializeSearch(searchInterface, searchInterfaceOptions, isSecureSearch) {
    let coveoAccessToken = searchInterfaceOptions.coveoAccessToken;
    let coveoOrganizationId = searchInterfaceOptions.coveoOrganizationId;
    let pipelineContext = searchInterfaceOptions.pipelineContext;
    let translations = searchInterfaceOptions.translations;
    let thesaurus = searchInterfaceOptions.thesaurus;
    let isGenaiEnabled = searchInterfaceOptions.isGenaiEnabled;

    const coveoSettings = {
        accessToken: coveoAccessToken,
        organizationId: coveoOrganizationId,
        analytics: {
            analyticsMode: 'legacy',
            anonymous: true,
            analyticsClientMiddleware: (eventType, payload) => {
                if (eventType === 'search' && payload.responseTime) {
                    const totalCount = searchInterface.engine.state.search.response.totalCount;
                    payload.customData.totalCount = totalCount;
                    pushToGoogleDataLayer(`coveo-${eventType}`, 'coveoAnalyticsEventData', payload);
                }

                return payload;
            },
        },
        preprocessRequest: (request, clientOrigin) => {
            if (clientOrigin === 'searchApiFetch') {
                const body = JSON.parse(request.body);

                if (body.hasOwnProperty('facets')) {
                    body.facets.forEach(facet => {
                        facet.currentValues.forEach(facetValue => {
                            facetValue.value = coveoPlusReplace(facetValue.value);
                            return facetValue;
                        });
                    });
                }

                if (isGenaiEnabled && thesaurus && body.hasOwnProperty('q')) {
                    let result = body.q;

                    for (const [ key, value ] of Object.entries(JSON.parse(thesaurus))) {
                        const valueRegex = new RegExp(escapeRegExp(value), 'gi');
                        const keyRegex = new RegExp(`\\b${escapeRegExp(key)}\\b`, 'gi');

                        const parts = result.split(valueRegex);
                        const replacedParts = parts.map(part => part.replace(keyRegex, value));
                        result = replacedParts.join(value);
                    }

                    body.q = result;
                }

                request.body = JSON.stringify(body);
            }

            return request;
        },

        search: {
            preprocessSearchResponseMiddleware: response => {
                response.body.facets.forEach(facet => {
                    facet.values.forEach(facetValue => {
                        facetValue.value = coveoPlusRestore(facetValue.value);
                        return facetValue;
                    });
                });
                return response;
            },
            preprocessFacetSearchResponseMiddleware: response => {
                response.body.values.forEach(facetValue => {
                    facetValue.displayValue = coveoPlusRestore(facetValue.displayValue);
                    facetValue.rawValue = coveoPlusRestore(facetValue.rawValue);
                    return facetValue;
                });
                return response;
            },
            preprocessQuerySuggestResponseMiddleware: response => response,
        },
    };


    /** Generate the token + renew the expired token */
    if (isSecureSearch) {
        const generateToken = async isNonRenewFlag => {
            try {
                const lsTest = () => {
                    const test = 'test';
                    try {
                        sessionStorage.setItem(test, test);
                        sessionStorage.removeItem(test);
                        return true;
                    } catch (e) {
                        return false;
                    }
                };

                const storageIsSupported = lsTest();

                /** store in session storage else store in window object */
                if (!(storageIsSupported && sessionStorage.getItem('coveoSecureToken')) || !isNonRenewFlag) {
                    let origin = window.location.origin.replace('www.amd.com', 'account.amd.com');
                    const data = await safeFetch(`${origin}/bin/protected/amd/searchtoken`,
                        { credentials: 'include' });
                    if (Object.keys(data).length > 0) {
                        if (data.hasOwnProperty('token')) {
                            if (storageIsSupported) {
                                sessionStorage.setItem('coveoSecureToken', data.token);
                            } else {
                                window.coveoSecureToken = data.token;
                            }

                            return Promise.resolve(data.token);
                        }
                        throw new Error('JSON response does not have token attribute');
                    } else {
                        throw new Error('Error generating JSON response');
                    }
                } else {
                    if (storageIsSupported && sessionStorage.getItem('coveoSecureToken') !== null) {
                        return Promise.resolve(sessionStorage.getItem('coveoSecureToken'));
                    }
                    return Promise.resolve(window.coveoSecureToken);
                }
            } catch (error) {
                return Promise.resolve(coveoAccessToken);
            }
        };

        const coveoToken = await generateToken(true);
        coveoSettings.accessToken = coveoToken;
        coveoSettings.renewAccessToken = generateToken;
    }


    await searchInterface.initialize(coveoSettings);

    /** Disable the coveo analytics if performance cookies are blocked */
    let { consentCheck, PERFORMANCE } = await import('../general/consentCheck.js');
    let getConsent = consentCheck([ PERFORMANCE ]);
    if (!getConsent) {
        searchInterface.analytics = false;
    }

    /** Add i18n for coveo search captions (facets) */
    if (pipelineContext && translations) {
        const map = new Map(Object.entries(JSON.parse(translations)));
        for (let [ key, value ] of map) {
            searchInterface.i18n.addResourceBundle(pipelineContext, key, value);
        }
    }
}

/** Public Search */
async function initializeSearchInterface(searchInterface, searchInterfaceOptions) {
    await initializeSearch(searchInterface, searchInterfaceOptions, false);
}

/** Secure Search */
async function initializeSecureSearchInterface(searchInterface, searchInterfaceOptions) {
    await initializeSearch(searchInterface, searchInterfaceOptions, true);
}

/**
 * Sets the query context for AMD language localization.
 * @param {Object} engine - The Coveo search engine instance
 * @param {string} contextValue - The language context value to set
 * @returns {Promise<void>} Promise that resolves when context is set
 *
 * @example
 * await setContext(engine, "en-US");
 */
async function setContext(engine, contextValue) {
    if (contextValue) {
        let { loadContextActions } = await import(/* webpackChunkName: 'coveoheadless' */
            '@coveo/headless');
        const action = loadContextActions(engine).addContext({
            contextKey: 'amd_lang',
            contextValue: contextValue
        });
        engine.dispatch(action);
    }
}

/**
 * Registers an advanced query expression without exposing it in URL hash parameters.
 * @param {Object} engine - The Coveo search engine instance
 * @param {string} expression - The advanced query expression to register
 * @returns {Promise<void>} Promise that resolves when expression is registered
 *
 * @example
 * await setAdvancedQueryExpression(engine, "@source==Documentation");
 */
async function setAdvancedQueryExpression(engine, expression) {
    if (expression) {
        let { loadAdvancedSearchQueryActions } = await import(/* webpackChunkName: 'coveoheadless' */
            '@coveo/headless');
        const action = loadAdvancedSearchQueryActions(
            engine
        ).registerAdvancedSearchQueries({
            cq: expression
        });
        engine.dispatch(action);
    }
}

/**
 * Updates an advanced query expression and exposes it in URL hash parameters.
 * @param {Object} engine - The Coveo search engine instance
 * @param {string} expression - The advanced query expression to update
 * @returns {Promise<void>} Promise that resolves when expression is updated
 *
 * @example
 * await updateAdvancedQueryExpression(engine, "@filetype==pdf");
 */
async function updateAdvancedQueryExpression(engine, expression) {
    if (expression) {
        let { loadAdvancedSearchQueryActions } = await import(/* webpackChunkName: 'coveoheadless' */
            '@coveo/headless');
        const action = loadAdvancedSearchQueryActions(
            engine
        ).updateAdvancedSearchQueries({
            cq: expression
        });
        engine.dispatch(action);
    }
}

/**
 * Registers field sort criteria without exposing it in URL hash parameters.
 * @param {Object} engine - The Coveo search engine instance
 * @param {string} sortField - The field name to sort by
 * @param {string} sortOrder - The sort order ('ascending' or 'descending')
 * @returns {Promise<void>} Promise that resolves when sort criteria is registered
 *
 * @example
 * await setFieldSort(engine, "date", "descending");
 */
async function setFieldSort(engine, sortField, sortOrder) {
    if (sortField) {
        let { loadSortCriteriaActions } = await import('@coveo/headless');
        const action = loadSortCriteriaActions(
            engine
        ).registerSortCriterion(
            [ { by: 'field', field: sortField, order: sortOrder } ]
        );

        engine.dispatch(action);
    }
}

/**
 * Updates field sort criteria and exposes it in URL hash parameters.
 * @param {Object} engine - The Coveo search engine instance
 * @param {string} sortField - The field name to sort by
 * @param {string} sortOrder - The sort order ('ascending' or 'descending')
 * @returns {Promise<void>} Promise that resolves when sort criteria is updated
 *
 * @example
 * await updateFieldSort(engine, "title", "ascending");
 */
async function updateFieldSort(engine, sortField, sortOrder) {
    if (sortField) {
        let { loadSortCriteriaActions } = await import('@coveo/headless');
        const action = loadSortCriteriaActions(
            engine
        ).updateSortCriterion(
            [ { by: 'field', field: sortField, order: sortOrder } ]
        );

        engine.dispatch(action);
    }
}

/**
 * Registers the number of results per page without exposing it in URL hash parameters.
 * @param {Object} engine - The Coveo search engine instance
 * @param {number|string} numberOfResults - The number of results to display per page
 * @returns {Promise<void>} Promise that resolves when number of results is registered
 *
 * @example
 * await setNumberOfResults(engine, 20);
 */
async function setNumberOfResults(engine, numberOfResults) {
    if (numberOfResults) {
        let { loadPaginationActions } = await import('@coveo/headless');
        const action = loadPaginationActions(
            engine
        ).registerNumberOfResults(
            Number(numberOfResults)
        );

        engine.dispatch(action);
    }
}

/**
 * Updates the number of results per page and exposes it in URL hash parameters.
 * @param {Object} engine - The Coveo search engine instance
 * @param {number|string} numberOfResults - The number of results to display per page
 * @returns {Promise<void>} Promise that resolves when number of results is updated
 *
 * @example
 * await updateNumberOfResults(engine, 50);
 */
async function updateNumberOfResults(engine, numberOfResults) {
    if (numberOfResults) {
        let { loadPaginationActions } = await import('@coveo/headless');
        const action = loadPaginationActions(
            engine
        ).updateNumberOfResults(
            Number(numberOfResults)
        );

        engine.dispatch(action);
    }
}

export { initializeSecureSearchInterface, initializeSearchInterface, setAdvancedQueryExpression,
    updateAdvancedQueryExpression, setContext, setFieldSort, updateFieldSort, setNumberOfResults,
    updateNumberOfResults };
